import { getUniqueValues } from "@sazka/utils/get-unique-values";
import { KAMENY_COMBOS } from "../data/kameny-constants";

/**
 * Provides utility functions for the Kameny lottery.
 */
export namespace KamenyUtils {
    /**
     * Maps raw Kameny draw numbers (dice results < 32) and figures (>= 32) to display objects.
     * Uses the KAMENY_COMBOS constants directly to find text labels for figures.
     * @param numbers The array of drawn numbers.
     * @returns An object with display data for normal numbers and figures.
     */
    export const mapKamenyNumbers = (numbers: number[]) => {
        const nums = numbers.filter((val) => val < 32).map((val) => ({ val, text: `${val + 5}` }));
        const figures = numbers
            .filter((value) => value >= 32)
            ?.map((value) => KAMENY_COMBOS.find((item) => item.id === value))
            .toSorted((a, b) => a.summaryDisplayOrder - b.summaryDisplayOrder)
            .map((combo) => {
                return { val: combo.id, text: combo?.text ?? "" };
            });
        return { numbers: nums, figures };
    };

    /**
     * Determines which Kameny figure combos occur in a 6-dice roll.
     * If a figure is detected, it fetches the matching text label from KAMENY_COMBOS.
     * @param diceRolls An array of exactly 6 dice values, each 1-6.
     * @returns An array of figure labels.
     */
    export const getKamenyFiguresFromDiceRolls = (diceRolls: number[]): string[] => {
        if (diceRolls?.length !== 6) return [];

        const set = getUniqueValues(diceRolls);

        const isSixSame = set.some((x) => x.count === 6);
        const isFiveSame = set.some((x) => x.count === 5);
        const isFourSame = set.some((x) => x.count === 4);
        const isTwoTriplets = set.filter((x) => x.count === 3).length === 2;
        const isThreePairs = set.filter((x) => x.count === 2).length === 3;
        const isStraight = set.length === 6;

        const isOnes = diceRolls.every((x) => x === 1);
        const isTwos = diceRolls.every((x) => x === 2);
        const isThrees = diceRolls.every((x) => x === 3);
        const isFours = diceRolls.every((x) => x === 4);
        const isFives = diceRolls.every((x) => x === 5);
        const isSixes = diceRolls.every((x) => x === 6);

        const isEven = diceRolls.every((x) => x % 2 === 0);
        const isOdd = diceRolls.every((x) => x % 2 === 1);

        const isLow = diceRolls.every((x) => x <= 3);
        const isHigh = diceRolls.every((x) => x >= 4);

        return [
            isSixSame,
            isFiveSame,
            isFourSame,
            isTwoTriplets,
            isThreePairs,
            isStraight,
            isOnes,
            isTwos,
            isThrees,
            isFours,
            isFives,
            isSixes,
            isEven,
            isOdd,
            isLow,
            isHigh,
        ]
            .map((flag, index) => (flag ? 32 + index : null))
            .filter((id): id is number => id !== null)
            .map((id) => {
                const combo = KAMENY_COMBOS.find((item) => item.id === id);
                return combo?.text ?? "";
            });
    };
}
